import { useRestoreSkill } from "@app/lib/swr/skill_configurations";
import type { SkillWithoutInstructionsAndToolsType } from "@app/types/assistant/skill_configuration";
import type { LightWorkspaceType } from "@app/types/user";
import {
  Dialog,
  DialogContainer,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@dust-tt/sparkle";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";

interface RestoreSkillDialogProps {
  skill: SkillWithoutInstructionsAndToolsType;
  isOpen: boolean;
  onClose: () => void;
  owner: LightWorkspaceType;
}

export function RestoreSkillDialog({
  skill,
  isOpen,
  onClose,
  owner,
}: RestoreSkillDialogProps) {
  const { t } = useTranslation();
  const [isRestoring, setIsRestoring] = useState(false);
  const doRestore = useRestoreSkill({
    owner,
    skill: skill,
  });

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent size="md" isAlertDialog>
        <DialogHeader hideButton>
          <DialogTitle>
            {t("builder.skillRestoringTitle", "Restoring the skill")}
          </DialogTitle>
          <DialogDescription>
            <div>
              <Trans
                i18nKey="builder.skillRestoreConfirmBody"
                defaults="This will restore the skill <0>{{name}}</0> for everyone."
                values={{ name: skill.name }}
                components={[<span key="0" className="font-bold" />]}
              />
            </div>
          </DialogDescription>
        </DialogHeader>
        <DialogContainer>
          <div className="font-bold">
            {t(
              "builder.skillActionAreYouSure",
              "Are you sure you want to proceed?"
            )}
          </div>
        </DialogContainer>
        <DialogFooter
          leftButtonProps={{
            label: t("builder.skillCancelButton", "Cancel"),
            disabled: isRestoring,
            variant: "outline",
          }}
          rightButtonProps={{
            label: t("builder.skillRestoreTheSkill", "Restore the skill"),
            disabled: isRestoring,
            variant: "warning",
            onClick: async (e: React.MouseEvent) => {
              e.preventDefault();
              setIsRestoring(true);
              await doRestore();
              setIsRestoring(false);
              onClose();
            },
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
